<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useHelpers, useImageCompressor, useTranslate } from '@/shared/lib'
import useValidate from '@/shared/lib/validate'
import { useConfirm, VFilePickerField, VIcons } from '@/shared/ui'
import {
  useLoadInventObjectFileBlob,
  useLoadInventObjectFiles,
  useRemoveInventObjectFile,
  useUploadInventObjectFile,
} from '../model/api.ts'
import { isImageFileName } from '../model/helpers.ts'
import type { InventoryMapInfoFile } from '../model/types.ts'

const props = defineProps<{
  objectId: number
  enabled: boolean
  canEdit: boolean
}>()

const PHOTO_LIMIT_MB = 15
const FILE_LIMIT_MB = 30

const objectIdRef = computed(() => props.objectId)
const enabledRef = computed(() => props.enabled)

const { data: files, refetch } = useLoadInventObjectFiles(objectIdRef, enabledRef)
const { formatFileSize, saveFile } = useHelpers()
const { trs } = useTranslate()
const { show } = useConfirm()
const { fileTypes, fileSize } = useValidate()

/** Правила проверки выбранных файлов перед загрузкой */
const rules = [
  (value: File[]) =>
    fileTypes(value, [
      'image/png',
      'image/jpeg',
      'image/heic',
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/zip',
      'application/vnd.rar',
      'application/x-7z-compressed',
    ]),
  (value: File[]) => fileSize(value, PHOTO_LIMIT_MB, FILE_LIMIT_MB),
]

/** Загрузка выбранных файлов — новая точка принимает файлы по одному,
 *  поэтому несколько файлов загружаются последовательно. */
const { upload, isPending: isUploading } = useUploadInventObjectFile()
const pickedFiles = ref<File[]>([])
const isCompressing = ref(false)

const compressImages = async (list: File[]): Promise<File[]> => {
  isCompressing.value = true
  const result: File[] = []

  for (const item of list) {
    if (item.type.startsWith('image/')) {
      result.push(await useImageCompressor.compressImage(item, 1920))
    } else {
      result.push(item)
    }
  }

  isCompressing.value = false
  return result
}

const uploadHandler = async (list: File[]) => {
  if (!list.length) return

  const compressed = await compressImages(list)
  for (const file of compressed) {
    await upload(props.objectId, file)
  }

  pickedFiles.value = []
  await refetch()
}

watch(pickedFiles, (list) => {
  if (list.length) void uploadHandler(list)
})

const isLoading = computed(() => isCompressing.value || isUploading.value)

const filesInfo = computed(() => ({
  count: pickedFiles.value.length,
  size: formatFileSize(pickedFiles.value.reduce((sum, item) => sum + item.size, 0)),
}))

/** Удаление файла */
const { remove } = useRemoveInventObjectFile()
const removeHandler = (file: InventoryMapInfoFile) => {
  show({
    title: trs('itf.remove_file'),
    confirmFunc: async () => {
      const ok = await remove(props.objectId, file.id)
      if (ok) await refetch()
    },
  })
}

/** Просмотр/скачивание — точка требует `Authorization`, поэтому грузим байты,
 *  а не открываем прямую ссылку. */
const { load: loadBlob } = useLoadInventObjectFileBlob()

const openHandler = async (file: InventoryMapInfoFile) => {
  const blob = await loadBlob(props.objectId, file.id)
  if (!blob) return
  window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer')
}

const downloadHandler = async (file: InventoryMapInfoFile) => {
  const blob = await loadBlob(props.objectId, file.id)
  if (!blob) return
  saveFile(URL.createObjectURL(blob), file.name)
}

/** Превью изображений в карусели — блобы подгружаются лениво по мере появления файлов */
const imagePreviews = ref<Record<number, string>>({})
watch(
  files,
  async (list) => {
    for (const file of list ?? []) {
      if (isImageFileName(file.name) && !imagePreviews.value[file.id]) {
        const blob = await loadBlob(props.objectId, file.id)
        if (blob) imagePreviews.value[file.id] = URL.createObjectURL(blob)
      }
    }
  },
  { immediate: true },
)

const images = computed(() =>
  (files.value ?? [])
    .filter((file) => isImageFileName(file.name) && imagePreviews.value[file.id])
    .map((file) => imagePreviews.value[file.id]),
)
</script>

<template>
  <div class="w-invent-map-info-item-files">
    <div v-if="images.length" class="w-invent-map-info-item-files__images mb-4">
      <VCarousel height="auto" hide-delimiters>
        <template #prev="{ props: navProps }">
          <VBtn
            @click="navProps.onClick"
            color="grey"
            variant="outlined"
            class="w-invent-map-info-item-files__arrow"
            icon="chevron_left"
          />
        </template>

        <template #next="{ props: navProps }">
          <VBtn
            @click="navProps.onClick"
            color="grey"
            variant="outlined"
            class="w-invent-map-info-item-files__arrow"
            icon="chevron_right"
          />
        </template>

        <VCarouselItem v-for="img in images" :key="img">
          <img :src="img" alt="preview" />
        </VCarouselItem>
      </VCarousel>
    </div>

    <div class="w-invent-map-info-item-files__label">
      {{ $t('itf.added_files') }}

      <VTooltip
        :text="$t('itf.file_limits', { photo_limit: PHOTO_LIMIT_MB, file_limit: FILE_LIMIT_MB })"
        location="top"
      >
        <template #activator="{ props: tipProps }">
          <VIcons type="info" v-bind="tipProps" />
        </template>
      </VTooltip>
    </div>

    <div v-if="(files || []).length" class="w-invent-map-info-item-files__list mb-4">
      <div v-for="file in files" :key="file.id" class="w-invent-map-info-item-files__item">
        <VIcons
          class="w-invent-map-info-item-files__item-icon"
          :type="isImageFileName(file.name) ? 'image' : 'article'"
        />

        <div class="w-invent-map-info-item-files__item-name">
          <p>{{ file.name }}</p>
          <span>{{ formatFileSize(file.size_bytes) }}</span>
        </div>

        <div class="w-invent-map-info-item-files__options">
          <VSpeedDial location="left center" transition="scale-transition">
            <template #activator="{ props: activatorProps }">
              <VBtn color="default" v-bind="activatorProps" icon density="compact">
                <VIcons type="more_vert" />
              </VBtn>
            </template>

            <VBtn
              v-if="canEdit"
              key="1"
              color="error"
              icon
              density="compact"
              @click="removeHandler(file)"
            >
              <VIcons type="delete" />
            </VBtn>

            <VFab key="2" color="primary" icon density="compact" @click="downloadHandler(file)">
              <VIcons type="download" />
            </VFab>

            <VTooltip location="top" :text="$t('itf.view')">
              <template #activator="{ props: tipProps }">
                <VFab
                  key="3"
                  color="primary"
                  icon
                  density="compact"
                  v-bind="tipProps"
                  @click="openHandler(file)"
                >
                  <VIcons type="visibility" />
                </VFab>
              </template>
            </VTooltip>
          </VSpeedDial>
        </div>
      </div>
    </div>

    <div :class="{ disabled: !canEdit }">
      <VFilePickerField
        v-model="pickedFiles"
        :label="$t('itf.attach_file')"
        :rules="rules"
        :loading="isLoading"
        :disabled="!canEdit"
        multiple
      >
        <template #selection>
          {{ $t('itf.file_selection', filesInfo) }}
        </template>
      </VFilePickerField>
    </div>
  </div>
</template>
